/**
 * Invoice numbers (rule 46(b) of the CGST Rules): at most 16 characters, made of letters, digits, the
 * hyphen "-" and the slash "/", unique within the financial year. The GST portal treats "inv-1" and
 * "INV-1" as the same number, so uniqueness is checked on the upper-case form.
 */

export const INVOICE_NUMBER_MAX = 16;
const ALLOWED = /^[A-Za-z0-9/-]+$/;

export type InvoiceNumberProblem = 'empty' | 'too_long' | 'characters' | 'zero';

export function invoiceNumberProblem(number: string): InvoiceNumberProblem | null {
  if (!number) return 'empty';
  if (number.length > INVOICE_NUMBER_MAX) return 'too_long';
  if (!ALLOWED.test(number)) return 'characters';
  if (/^[0/-]+$/.test(number)) return 'zero';
  return null;
}

/** Trimmed, with the spaces people type around separators taken out: " inv - 2404 " → "inv-2404". */
export function tidyInvoiceNumber(raw: string): string {
  return raw.trim().replace(/\s*([/-])\s*/g, '$1');
}

/** The form uniqueness is checked on. */
export const invoiceNumberKey = (number: string) => number.toUpperCase();

/** A series is a fixed prefix and a running number of a fixed width: "INV-2404" is "INV-" + 2404. */
export interface NumberParts {
  prefix: string;
  /** The running number (null when the number doesn't end in digits). */
  serial: number | null;
  /** Digits in the running number, so "INV-0042" stays in its own series with width 4. */
  width: number;
}

export function numberParts(number: string): NumberParts {
  const m = /^(.*?)(\d+)$/.exec(number);
  if (!m) return { prefix: number, serial: null, width: 0 };
  return { prefix: m[1] ?? '', serial: Number(m[2]), width: (m[2] ?? '').length };
}

/**
 * The number someone probably means when they type only part of it, given the numbers already used:
 * "2405" or "inv 2405" after INV-2404 → "INV-2405". Null when there's nothing to go by, or the typed
 * number already looks complete.
 */
export function completeInvoiceNumber(typed: string, used: readonly string[]): string | null {
  const tidy = tidyInvoiceNumber(typed);
  const m = /^([A-Za-z]*)[\s-]*(\d+)$/.exec(tidy.replace(/\s+/g, ' '));
  if (!m) return null;
  const [, letters = '', digits = ''] = m;
  const latest = [...used].reverse().find((u) => {
    const parts = numberParts(u);
    return (
      parts.serial !== null &&
      (letters === '' || parts.prefix.replace(/[^A-Za-z]/g, '').toUpperCase() === letters.toUpperCase())
    );
  });
  if (!latest) return letters ? `${letters.toUpperCase()}-${digits}` : null;
  const parts = numberParts(latest);
  // A shorter number only belongs to a series written with leading zeros ("42" → "0042"); "118" isn't
  // one of INV-2401…2403.
  const zeroPadded = String(parts.serial).length < parts.width;
  if (!letters && digits.length !== parts.width && !(zeroPadded && digits.length < parts.width)) return null;
  const candidate = `${parts.prefix}${digits.padStart(parts.width, '0')}`;
  return candidate.toUpperCase() === tidy.toUpperCase() ? null : candidate;
}

/**
 * The number to store for what someone typed: completed from the series in use ("2405" → "INV-2405"),
 * written the way the series is when it's one of them ("inv-2405" → "INV-2405"), else tidied with its
 * letters in capitals.
 */
export function resolveInvoiceNumber(typed: string, used: readonly string[]): string {
  const completed = completeInvoiceNumber(typed, used);
  if (completed) return completed;
  const tidy = tidyInvoiceNumber(typed).replace(/\s+/g, '');
  const parts = numberParts(tidy);
  const series = [...used].reverse().find((u) => numberParts(u).prefix.toUpperCase() === parts.prefix.toUpperCase());
  return series ? `${numberParts(series).prefix}${tidy.slice(parts.prefix.length)}` : tidy.toUpperCase();
}

/** A run of numbers in one series, for table 13 (documents issued). */
export interface DocumentSeries {
  from: string;
  to: string;
  /** How many numbers of the series were used, the cancelled ones too. */
  total: number;
  cancelled: number;
  net: number;
}

/**
 * The series a set of invoice numbers makes, as table 13 reports them: per prefix (and width), the lowest
 * and highest number, how many were used and how many of those were cancelled (Phase 16: walk-in bills).
 * Numbers that don't end in digits are series of their own. A number is counted once, whatever its case.
 */
export function documentSeries(numbers: readonly string[], cancelled: readonly string[] = []): DocumentSeries[] {
  const cancelledKeys = new Set(cancelled.map(invoiceNumberKey));
  const seen = new Set<string>();
  const groups = new Map<string, { number: string; serial: number }[]>();
  const single: string[] = [];
  for (const number of [...numbers, ...cancelled]) {
    const key = invoiceNumberKey(number);
    if (seen.has(key)) continue;
    seen.add(key);
    const parts = numberParts(number);
    if (parts.serial === null) {
      single.push(number);
      continue;
    }
    const group = `${parts.prefix.toUpperCase()}\u0000${parts.width}`;
    groups.set(group, [...(groups.get(group) ?? []), { number, serial: parts.serial }]);
  }
  const counted = (members: readonly { number: string }[]) =>
    members.filter((m) => cancelledKeys.has(invoiceNumberKey(m.number))).length;
  const series: DocumentSeries[] = [];
  for (const members of groups.values()) {
    members.sort((a, b) => a.serial - b.serial);
    const first = members[0] as { number: string };
    const last = members[members.length - 1] as { number: string };
    const gone = counted(members);
    series.push({
      from: first.number,
      to: last.number,
      total: members.length,
      cancelled: gone,
      net: members.length - gone,
    });
  }
  for (const number of single.sort()) {
    const gone = counted([{ number }]);
    series.push({ from: number, to: number, total: 1, cancelled: gone, net: 1 - gone });
  }
  return series.sort((a, b) => a.from.localeCompare(b.from));
}

/** The most bills one range may hold: more than a busy shop's month (100 a day). */
export const MAX_RANGE = 5_000;

export type RangeProblem =
  /** A number the portal wouldn't take, or one that doesn't end in digits. */
  | 'invalid'
  /** The two ends are in different series ("A-1" to "B-9"). */
  | 'series'
  | 'backwards'
  | 'too_many';

/**
 * Every number from `from` to `to`, in their series: "2401" to "2450" is 50 numbers, "A/001" to "A/120"
 * keeps the zeros, "1" to "120" doesn't. Both ends must be written as the series writes them.
 */
export function numbersInRange(
  from: string,
  to: string,
  max: number = MAX_RANGE,
): { numbers: string[] } | { problem: RangeProblem } {
  if (invoiceNumberProblem(from) || invoiceNumberProblem(to)) return { problem: 'invalid' };
  const a = numberParts(from);
  const b = numberParts(to);
  if (a.serial === null || b.serial === null) return { problem: 'invalid' };
  if (a.prefix.toUpperCase() !== b.prefix.toUpperCase()) return { problem: 'series' };
  // "0042" is written with zeros to a width; "42" isn't, so it runs on to "120".
  const padded = String(a.serial).length < a.width;
  if (padded && b.width !== a.width) return { problem: 'series' };
  if (b.serial < a.serial) return { problem: 'backwards' };
  if (b.serial - a.serial + 1 > max) return { problem: 'too_many' };
  const numbers: string[] = [];
  for (let n = a.serial; n <= b.serial; n++) {
    const number = `${a.prefix}${padded ? String(n).padStart(a.width, '0') : n}`;
    if (invoiceNumberProblem(number)) return { problem: 'invalid' };
    numbers.push(number);
  }
  return { numbers };
}
